import React from "react";
import { toastr } from "react-redux-toastr";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import {
	FiCalendar,
	FiTrash2,
	FiArrowLeft,
	FiChevronLeft,
	FiChevronRight,
	FiEdit2,
	FiClock,
	FiX,
} from "react-icons/fi";
import { MdPlaylistAddCheck } from "react-icons/md";
import Dropzone from "react-dropzone";
import moment from "moment";
import {
	FormGroup,
	FormControl,
	FormLabel,
	FormCheck,
	Col,
	Row,
} from "react-bootstrap";
import DatePicker from "components/Shared/DatePicker";

import { workflowsService } from "services";
import { wfType, wfStatus } from "services/workflowsService";
import { shiftTypeQuery } from "services/shiftsService";
import Avatar from "components/Avatar";
import SimpleModal from "components/Modal/SimpleModal";
import EmployeeSelectSearch from "components/EmployeeSelectSearch/EmployeeSelectSearch";
import TimeInputWithArrows from "components/TimeInputWithArrows";

import {
	errorFormating,
	normalizeDecimal,
	isAdmin,
	isSupervisor,
} from "utils/utils";
import SubmitButton from "components/SubmitButton";
import * as workflowsActions from "actions/workflowsActions";
import * as employeesActions from "actions/employeesActions";
import * as shiftsActions from "actions/shiftsActions";

const DATE_KEY_FORMAT = "YYYY-MM-DD";

const getInitialSelectedDates = (start, end) => {
	if (!start) return [];
	const selected = [];
	const last = moment(end || start, DATE_KEY_FORMAT);
	for (
		let day = moment(start, DATE_KEY_FORMAT);
		day.isSameOrBefore(last, "day");
		day.add(1, "day")
	) {
		selected.push(day.format(DATE_KEY_FORMAT));
	}
	return selected;
};

class AbsenceEdit extends React.Component {
	constructor(props) {
		super(props);
		// console.log(props);
		const requester_id = props.user.id;
		this.employeeSelectFetchOptions = { enabled: true };

		let absence = {
			type: wfType.absences,
			isSingleDay: true,
			shift_id: "",
			status: wfStatus.pending,
			start: null,
			end: null,
			requester_id,
			attachments: [],
			workflow_hourly_requested: false,
		};

		let isEdit = false;

		if (props.absence) {
			absence = { ...props.absence };
			isEdit = true;
			absence.isSingleDay =
				absence.end === null || absence.end === "" || absence.end === undefined;
			absence.shift_id = absence.shift.id;
		}

		const initialDate = moment(absence.start || undefined);
		const initialSelectedDates = isEdit
			? getInitialSelectedDates(absence.start, absence.end)
			: [];
		const initialPeriods = isEdit
			? [{ start: absence.start || null, end: absence.end || absence.start || null }]
			: [];
		const hourlyEnd =
			absence.workflow_hourly_requested && absence.start_hour && absence.duration
				? moment
						.utc(absence.start_hour)
						.local()
						.add(Number(absence.duration), "hours")
						.format("HH:mm")
				: "";

		this.state = {
			isEdit,
			isLoading: false,
			submitted: false,
			errors: {},
			absence,
			files: [],
			disabled: props.disabled ? true : false,
			// Overlap detection
			overlappingByDate: {}, // { "YYYY-MM-DD": [workflow, ...] }
			isLoadingOverlap: false,
			// New fields for creation on behalf of others
			creationStep:
				!isEdit && (isAdmin() || isSupervisor()) ? "select" : "form",
			creationTarget: "self", // "self" or "employee"
			selectedEmployeeId: null,
			absencePeriods: initialPeriods,
			selectedDates: initialSelectedDates,
			calendarMonth: initialDate.month(),
			calendarYear: initialDate.year(),
			calendarLoadedFor: null,
			calendarDrag: null,
			editingPeriod: null,
			editingPeriodOriginal: null,
			hourlyEnd,
		};

		this.skipCalendarClick = false;
	}

	componentDidMount() {
		this.isMountedFlag = true;
		const { absence, viewedEmployeeId, user } = this.props;
		if (this.state.isEdit) {
			this.refreshAbsenceDetails();
		}
		// Si el modal abre con fecha ya puesta (edición), disparar la comprobación
		if (this.state.absence.start) {
			this.checkGroupOverlap();
		}

		// Cargar turnos iniciales o empleado según el objetivo
		if (this.state.isEdit) {
			const requesterId = absence?.requester?.id || absence?.requester_id;
			if (requesterId === user.id) {
				this.props.getAbsenceShifts();
			} else if (requesterId) {
				this.props.getEmployee(requesterId);
			}
		} else if (this.state.creationStep === "form") {
			// Si entramos directamente al formulario (ej. empleado normal o admin en vista filtrada)
			if (this.state.creationTarget === "self") {
				this.props.getAbsenceShifts();
			} else {
				const employeeId = viewedEmployeeId || this.state.selectedEmployeeId;
				if (employeeId) {
					this.props.getEmployee(employeeId);
				}
			}
		}

		this.loadAbsenceCalendar(this.state.calendarYear);
		window.addEventListener("pointerup", this.finishCalendarDrag);
		window.addEventListener("pointercancel", this.finishCalendarDrag);
	}

	componentDidUpdate(prevProps, prevState) {
		if (
			prevState.selectedEmployeeId !== this.state.selectedEmployeeId ||
			prevState.calendarYear !== this.state.calendarYear
		) {
			this.loadAbsenceCalendar(this.state.calendarYear);
		}
	}

	componentWillUnmount() {
		this.isMountedFlag = false;
		window.removeEventListener("pointerup", this.finishCalendarDrag);
		window.removeEventListener("pointercancel", this.finishCalendarDrag);
	}

	refreshAbsenceDetails = async () => {
		const absenceId = this.state.absence?.id;
		if (!absenceId) return;

		const response = await workflowsService.getById(absenceId);
		if (!this.isMountedFlag || !response?.ok) return;

		const freshAbsence = response.data || response;
		const workflowStatus =
			typeof freshAbsence.status === "string"
				? freshAbsence.status
				: this.state.absence.status;
		const start = freshAbsence.start || null;
		const end = freshAbsence.end || start;
		const isSingleDay = !freshAbsence.end;
		const hourlyEnd =
			freshAbsence.workflow_hourly_requested &&
			freshAbsence.start_hour &&
			freshAbsence.duration
				? moment
						.utc(freshAbsence.start_hour)
						.local()
						.add(Number(freshAbsence.duration), "hours")
						.format("HH:mm")
				: "";

		this.setState({
			absence: {
				...freshAbsence,
				status: workflowStatus,
				isSingleDay,
				shift_id: freshAbsence.shift?.id || freshAbsence.shift_id || "",
			},
			absencePeriods: start ? [{ start, end }] : [],
			selectedDates: getInitialSelectedDates(start, freshAbsence.end),
			hourlyEnd,
		});
	};

	getCalendarEmployeeId = () => {
		const { absence, isEdit, creationTarget, selectedEmployeeId } = this.state;
		if (isEdit) return absence?.requester?.id || absence?.requester_id;
		if (creationTarget === "employee") return selectedEmployeeId;
		return this.props.user.id;
	};

	loadAbsenceCalendar = async (year) => {
		const employeeId = this.getCalendarEmployeeId();
		if (!employeeId) return;
		const key = `${employeeId}-${year}`;
		if (this.state.calendarLoadedFor === key) return;
		await this.props.getEmployeeAbsenceCalendar(employeeId, year);
		if (!this.isMountedFlag) return;
		this.setState({ calendarLoadedFor: key }, () => {
			if (!this.state.isEdit && !this.state.absence.workflow_hourly_requested) {
				this.setSelectedDates(
					this.state.selectedDates.filter((dateKey) =>
						this.isCalendarDateSelectable(dateKey)
					)
				);
			}
		});
	};

	handleSelectTarget = (target) => {
		this.setState(
			{
				creationTarget: target,
				creationStep: target === "self" ? "form" : "employee-select",
			},
			() => {
				if (target === "self") {
					this.props.getAbsenceShifts();
				}
			}
		);
	};

	handleEmployeeSelect = (event) => {
		const employeeId = event.target.value;
		this.setState({
			selectedEmployeeId: employeeId,
			creationStep: "form",
		});
	};

	handleChange = (event) => {
		const { absence } = this.state;

		let key = event.target.id;
		let value = event.target.value;

		this.setState({
			absence: {
				...absence,
				[key]: value,
			},
		});
	};

	handleTypeChange = (event) => {
		const { absence } = this.state;
		const id = event.target.id;
		const isHourly = id === "hours";
		const firstPeriod = this.state.absencePeriods[0];
		const nextState = {
			absence: {
				...absence,
				isSingleDay: false,
				workflow_hourly_requested: isHourly,
				start: isHourly
					? absence.start || moment().format(DATE_KEY_FORMAT)
					: firstPeriod?.start || null,
				end: isHourly ? null : firstPeriod?.end || null,
			},
		};

		if (isHourly && !absence.start_hour) {
			const defaultStart = moment().hour(9).minute(0).second(0).millisecond(0);
			nextState.absence = {
				...nextState.absence,
				start_hour: moment.utc(defaultStart.toDate()).format(),
			};
			nextState.hourlyEnd = "11:00";
		}

		this.setState(
			nextState,
			() => this.checkGroupOverlap()
		);
	};

	getHolidayMap = () => {
		if (!this.state.calendarLoadedFor) return {};
		return (this.props.employee_absence_calendar || []).reduce((holidays, day) => {
			if (day?.day && day?.day_type?.holiday) holidays[day.day] = day.day_type;
			return holidays;
		}, {});
	};

	isCalendarDateSelectable = (dateKey, allowPast = false) => {
		const day = moment(dateKey, DATE_KEY_FORMAT);
		if (!day.isValid()) return false;
		if (!allowPast && day.isBefore(moment().startOf("day"), "day")) return false;
		return !this.getHolidayMap()[dateKey];
	};

	getSelectableDateRange = (start, end, allowPast = false) => {
		if (!start) return [];
		let first = moment(start, DATE_KEY_FORMAT);
		let last = moment(end || start, DATE_KEY_FORMAT);
		if (last.isBefore(first, "day")) [first, last] = [last, first];
		const dates = [];
		for (let day = first.clone(); day.isSameOrBefore(last, "day"); day.add(1, "day")) {
			const key = day.format(DATE_KEY_FORMAT);
			if (this.isCalendarDateSelectable(key, allowPast)) dates.push(key);
		}
		return dates;
	};

	periodsFromDates = (dates) => {
		const sorted = Array.from(new Set(dates)).sort();
		const groups = [];
		sorted.forEach((dateKey) => {
			const current = groups[groups.length - 1];
			if (!current) {
				groups.push([dateKey]);
				return;
			}
			let next = moment(current[current.length - 1], DATE_KEY_FORMAT).add(1, "day");
			while (
				next.isBefore(moment(dateKey, DATE_KEY_FORMAT), "day") &&
				!this.isCalendarDateSelectable(next.format(DATE_KEY_FORMAT), true)
			) {
				next.add(1, "day");
			}
			if (next.format(DATE_KEY_FORMAT) === dateKey) current.push(dateKey);
			else groups.push([dateKey]);
		});
		return groups.map((group) => ({
			start: group[0],
			end: group[group.length - 1],
		}));
	};

	setSelectedDates = (selectedDates) => {
		const dates = Array.from(new Set(selectedDates)).sort();
		const absencePeriods = this.periodsFromDates(dates);
		const firstPeriod = absencePeriods[0] || { start: null, end: null };
		this.setState(
			{
				selectedDates: dates,
				absencePeriods,
				editingPeriod: null,
				absence: {
					...this.state.absence,
					start: firstPeriod.start,
					end: firstPeriod.end,
					isSingleDay: absencePeriods.length === 1 && firstPeriod.start === firstPeriod.end,
				},
			},
			() => this.checkGroupOverlap()
		);
	};

	toggleCalendarDate = (dateKey) => {
		if (this.state.disabled || this.state.isEdit || !this.isCalendarDateSelectable(dateKey)) return;
		const selected = new Set(this.state.selectedDates);
		if (selected.has(dateKey)) selected.delete(dateKey);
		else selected.add(dateKey);
		this.setSelectedDates(Array.from(selected));
	};

	startCalendarDrag = (dateKey, event) => {
		if (
			event.button !== 0 ||
			this.state.disabled ||
			this.state.isEdit ||
			this.state.absence.workflow_hourly_requested ||
			!this.isCalendarDateSelectable(dateKey)
		) return;
		this.setState({
			calendarDrag: {
				start: dateKey,
				current: dateKey,
				mode: this.state.selectedDates.includes(dateKey) ? "remove" : "add",
			},
		});
	};

	moveCalendarDrag = (dateKey) => {
		if (!this.state.calendarDrag || !this.isCalendarDateSelectable(dateKey)) return;
		if (this.state.calendarDrag.current === dateKey) return;
		this.setState({ calendarDrag: { ...this.state.calendarDrag, current: dateKey } });
	};

	finishCalendarDrag = () => {
		const drag = this.state.calendarDrag;
		if (!drag) return;
		this.setState({ calendarDrag: null });
		if (drag.start === drag.current) return;
		const selected = new Set(this.state.selectedDates);
		this.getSelectableDateRange(drag.start, drag.current).forEach((dateKey) => {
			if (drag.mode === "add") selected.add(dateKey);
			else selected.delete(dateKey);
		});
		this.skipCalendarClick = true;
		window.setTimeout(() => {
			this.skipCalendarClick = false;
		}, 0);
		this.setSelectedDates(Array.from(selected));
	};

	getEffectiveSelectedDates = () => {
		if (this.state.absence.workflow_hourly_requested) {
			return new Set(this.state.absence.start ? [this.state.absence.start] : []);
		}
		const selected = new Set(this.state.selectedDates);
		const drag = this.state.calendarDrag;
		if (drag && drag.start !== drag.current) {
			this.getSelectableDateRange(drag.start, drag.current).forEach((dateKey) => {
				if (drag.mode === "add") selected.add(dateKey);
				else selected.delete(dateKey);
			});
		}
		return selected;
	};

	changeCalendarMonth = (amount) => {
		const next = moment({
			year: this.state.calendarYear,
			month: this.state.calendarMonth,
			date: 1,
		}).add(amount, "month");
		this.setState({ calendarMonth: next.month(), calendarYear: next.year() });
	};

	handleCalendarClick = (dateKey) => {
		if (this.skipCalendarClick) return;
		if (this.state.absence.workflow_hourly_requested) {
			if (this.state.disabled || !this.isCalendarDateSelectable(dateKey)) return;
			this.setState(
				{
					absence: { ...this.state.absence, start: dateKey, end: null },
					selectedDates: [dateKey],
				},
				() => this.checkGroupOverlap()
			);
			return;
		}
		this.toggleCalendarDate(dateKey);
	};

	clearSelectedDates = () => this.setSelectedDates([]);

	handleDateChange = (id, date) => {
		const { absence } = this.state;

		let key = id;
		let value = date ? moment(date).format("YYYY-MM-DD") : null;

		const updatedAbsence = {
			...absence,
			[key]: value,
		};

		this.setState({ absence: updatedAbsence }, () => this.checkGroupOverlap());
	};

	handlePeriodDateChange = (index, field, date) => {
		const value = date ? moment(date).format("YYYY-MM-DD") : null;
		const absencePeriods = this.state.absencePeriods.map((period, i) =>
			i === index ? { ...period, [field]: value } : period
		);

		this.setState({ absencePeriods });
	};

	applyPeriodEdit = (index) => {
		const period = this.state.absencePeriods[index];
		if (!period?.start) return;
		const end = period.end || period.start;
		if (moment(end).isBefore(period.start, "day")) {
			this.setState({
				submitted: true,
				errors: { general: this.props.t("absences.form.periodEndBeforeStart") },
			});
			return;
		}
		const previous = this.state.editingPeriodOriginal || period;
		const selected = new Set(this.state.selectedDates);
		this.getSelectableDateRange(previous.start, previous.end, true).forEach((key) => selected.delete(key));
		this.getSelectableDateRange(period.start, end).forEach((key) => selected.add(key));
		this.setSelectedDates(Array.from(selected));
	};

	cancelPeriodEdit = () => {
		const { editingPeriod, editingPeriodOriginal, absencePeriods } = this.state;
		if (editingPeriod === null || !editingPeriodOriginal) {
			this.setState({ editingPeriod: null, editingPeriodOriginal: null });
			return;
		}
		this.setState({
			absencePeriods: absencePeriods.map((period, index) =>
				index === editingPeriod ? editingPeriodOriginal : period
			),
			editingPeriod: null,
			editingPeriodOriginal: null,
		});
	};

	removeAbsencePeriod = (index) => {
		const period = this.state.absencePeriods[index];
		const selected = new Set(this.state.selectedDates);
		this.getSelectableDateRange(period.start, period.end, true).forEach((key) => selected.delete(key));
		this.setSelectedDates(Array.from(selected));
	};

	checkGroupOverlap = async () => {
		const { absence } = this.state;
		const { user } = this.props;

		// Solo disparar si hay al menos fecha de inicio y el usuario es Admin o Supervisor
		const isMultiplePeriodCreation =
			!this.state.isEdit &&
			!absence.workflow_hourly_requested &&
			!absence.isSingleDay;
		const selectedPeriods = isMultiplePeriodCreation
			? this.state.absencePeriods.filter((period) => period.start)
			: absence.start
				? [{ start: absence.start, end: absence.end }]
				: [];

		if (selectedPeriods.length === 0 || (!isAdmin() && !isSupervisor())) {
			this.setState({ overlappingByDate: {} });
			return;
		}

		// El group_id viene en: user.employee_with_group.group.id
		const groupId = user?.employee_with_group?.group?.id;

		const start_from = selectedPeriods.reduce(
			(min, period) => (!min || period.start < min ? period.start : min),
			null
		);
		const start_to = selectedPeriods.reduce((max, period) => {
			const periodEnd = period.end || period.start;
			return !max || periodEnd > max ? periodEnd : max;
		}, null);

		this.setState({ isLoadingOverlap: true });

		try {
			const queryPayload = {
				start_from,
				start_to,
			};

			// Si es Admin/Supervisor, SÍ enviamos explícitamente el group_id para buscar en ese grupo (ya sea el propio o el asignado)
			// Si es un Empleado raso, OMITIMOS el group_id porque el RLS de Supabase bloquea peticiones con group_id explícito
			// dejando que el backend devuelva por defecto las ausencias permitidas para ese empleado (las de su grupo)
			if ((isAdmin() || isSupervisor()) && groupId) {
				queryPayload.group_id = groupId;
			}

			const res = await workflowsService.getGroupAbsencesForDates(queryPayload);

			if (res.ok && Array.isArray(res.data)) {
				// Mostrar a todos los compañeros excepto al propio usuario que consulta
				const colleagues = res.data.filter(
					(w) => w.requester && w.requester.id !== user.id
				);

				// Agrupar por día: para cada día del rango que toca cada ausencia
				const byDate = {};

				colleagues.forEach((w) => {
					const absStart = moment(w.start);
					const absEnd = w.end ? moment(w.end) : absStart.clone();

					// Iterar cada día del rango seleccionado
					selectedPeriods.forEach((period) => {
						const rangeStart = moment(period.start);
						const rangeEnd = moment(period.end || period.start);
						for (
							let d = rangeStart.clone();
							d.isSameOrBefore(rangeEnd, "day");
							d.add(1, "day")
						) {
						// ¿Este día toca la ausencia del compañero?
						if (
							d.isSameOrAfter(absStart, "day") &&
							d.isSameOrBefore(absEnd, "day")
						) {
							const key = d.format("YYYY-MM-DD");
							if (!byDate[key]) byDate[key] = [];
							// Evitar duplicados (un compañero puede aparecer una vez por día)
							if (!byDate[key].find((x) => x.requester.id === w.requester.id)) {
								byDate[key].push(w);
							}
						}
						}
					});
				});

				this.setState({ overlappingByDate: byDate });
			} else {
				this.setState({ overlappingByDate: {} });
			}
		} catch (e) {
			this.setState({ overlappingByDate: {} });
		} finally {
			this.setState({ isLoadingOverlap: false });
		}
	};

	handleTimeChange = (event) => {
		let rawValue = event.target.value;
		const { absence } = this.state;
		const today = new Date();
		let value_temp = rawValue.split(":");
		let value = "";

		if (rawValue !== "" && value_temp.length == 2) {
			value = new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate(),
				value_temp[0],
				value_temp[1]
			);

			const momentValue = moment.utc(value);
			value = momentValue.format();
		}
		this.setState({
			absence: {
				...absence,
				start_hour: value,
			},
		});
	};

	handleHourlyEndChange = (event) => {
		this.setState({ hourlyEnd: event.target.value });
	};

	checkValidField = (name) => {
		// console.log(this.state.submitted &&
		// 	this.state.errors[name] !== undefined &&
		// 	this.state.errors[name] !== "");
		return (
			this.state.submitted &&
			this.state.errors[name] !== undefined &&
			this.state.errors[name] !== ""
		);
	};

	handleSubmit = async (event) => {
		event.preventDefault();

		this.setState({ isLoading: true });
		let response = "";
		const props = this.props;
		const { absence, files, hourlyEnd } = this.state;
		var formData = new FormData();
		var hasErrors = false;
		var errorMsg = "";
		const hasFiles = files.length > 0;
		const isMultiplePeriodCreation =
			!this.state.isEdit &&
			!absence.workflow_hourly_requested &&
			!absence.isSingleDay;
		let failedPeriods = null;

		if (absence.workflow_hourly_requested && absence.start_hour && hourlyEnd) {
			const startTime = moment.utc(absence.start_hour).local();
			const endTime = moment(hourlyEnd, "HH:mm", true);
			const startMinutes = startTime.hours() * 60 + startTime.minutes();
			const endMinutes = endTime.hours() * 60 + endTime.minutes();
			if (!endTime.isValid() || endMinutes <= startMinutes) {
				this.setState({
					isLoading: false,
					submitted: true,
					errors: { end_hour: this.props.t("absences.form.endHourAfterStart") },
				});
				return;
			}
			absence.duration = (endMinutes - startMinutes) / 60;
		} else if (absence.workflow_hourly_requested && !hourlyEnd) {
			this.setState({
				isLoading: false,
				submitted: true,
				errors: { end_hour: this.props.t("absences.form.endHourRequired") },
			});
			return;
		}

		if (isMultiplePeriodCreation) {
			const periodWithoutStart = this.state.absencePeriods.findIndex(
				(period) => !period.start
			);
			const periodWithInvalidEnd = this.state.absencePeriods.findIndex(
				(period) => period.end && moment(period.end).isBefore(period.start, "day")
			);
			const sortedPeriods = this.state.absencePeriods
				.filter((period) => period.start)
				.map((period) => ({
					start: period.start,
					end: period.end || period.start,
				}))
				.sort((a, b) => a.start.localeCompare(b.start));
			let latestPeriodEnd = null;
			const hasOverlappingPeriods = sortedPeriods.some((period) => {
				if (latestPeriodEnd && period.start <= latestPeriodEnd) return true;
				latestPeriodEnd =
					!latestPeriodEnd || period.end > latestPeriodEnd
						? period.end
						: latestPeriodEnd;
				return false;
			});

			if (
				periodWithoutStart !== -1 ||
				periodWithInvalidEnd !== -1 ||
				hasOverlappingPeriods
			) {
				this.setState({
					isLoading: false,
					submitted: true,
					errors: {
						general:
							periodWithoutStart !== -1
								? this.props.t("absences.form.periodStartRequired")
								: periodWithInvalidEnd !== -1
									? this.props.t("absences.form.periodEndBeforeStart")
									: this.props.t("absences.form.periodsOverlap"),
					},
				});
				return;
			}
		}

		//console.log(files);
		//console.log(absence);

		// prepare workflow
		if (absence.isSingleDay) absence.end = null;

		if (
			absence.workflow_hourly_requested &&
			absence.start &&
			absence.start_hour
		) {
			const start_day = new Date(absence.start);
			const start_time = new Date(absence.start_hour);

			let start_frankenstein = new Date(
				start_day.getFullYear(),
				start_day.getMonth(),
				start_day.getDate(),
				start_time.getHours(),
				start_time.getMinutes()
			);

			const momentValue = moment.utc(start_frankenstein);
			start_frankenstein = momentValue.format();

			// console.log(start_frankenstein);
			absence.start_hour = start_frankenstein;
		}

		if (absence.duration)
			absence.duration = Number(normalizeDecimal(absence.duration));

		if (hasFiles) {
			files.map((file) => formData.append("filename", file));
		}

		// update workflow
		if (this.state.isEdit) {
			response = await workflowsService.update(absence);

			// si hay attachments temporales
			if (response.ok && hasFiles) {
				const responseAttachments = await workflowsService.updateAttachments(
					absence.id,
					formData
				);
				//console.log(responseAttachments);

				if (!responseAttachments.ok) {
					hasErrors = true;
					errorMsg = errorFormating(responseAttachments);
				}
			}

			if (response.ok && this.props.autoApproveOnSave) {
				const saved = response.data || absence;
				const approveObj = {
					...saved,
					...absence,
					id: saved.id || absence.id,
					status: wfStatus.approved,
					shift_id: absence.shift_id || absence.shift?.id,
				};
				const approveResponse = await workflowsService.update(approveObj);
				if (!approveResponse.ok) {
					hasErrors = true;
					errorMsg = errorFormating(approveResponse);
				}
			}
		} else {
			// Si es creación por admin de otro empleado o por admin para sí mismo
			if (isAdmin() || isSupervisor()) {
				if (
					this.state.creationTarget === "employee" &&
					this.state.selectedEmployeeId
				) {
					absence.requester_employee_id = this.state.selectedEmployeeId;
				}
			}

			const shouldAutoApprove =
				(isSupervisor() && this.state.creationTarget === "employee") ||
				(isAdmin() && this.state.creationTarget === "employee");

			if (isMultiplePeriodCreation) {
				const workflows = this.state.absencePeriods.map((period) => {
					const isSingleDay = !period.end || period.end === period.start;
					return {
						...absence,
						comments: absence.comments || null,
						// Los periodos se crean pendientes antes de la autoaprobación opcional.
						status: wfStatus.pending,
						start: period.start,
						end: isSingleDay ? null : period.end,
						isSingleDay,
						workflow_hourly_requested: false,
						attachments: [],
					};
				});

				response = await Promise.all(
					workflows.map(async (workflow) => {
						let createResponse;
						if (hasFiles) {
							const workflowFormData = new FormData();
							files.forEach((file) =>
								workflowFormData.append("filename", file)
							);
							workflowFormData.append(
								"workflow",
								new Blob([JSON.stringify(workflow)], {
									type: "application/json",
								}),
								"workflow.json"
							);

							createResponse =
								await workflowsService.addWithAttachments(workflowFormData);
						} else {
							// Crear cada periodo por la ruta estándar garantiza que se
							// persistan todos sus campos, incluidos los comentarios.
							createResponse = await workflowsService.add(workflow);
						}

						if (createResponse.ok && shouldAutoApprove) {
							const createdAbsence = createResponse.data || createResponse;
							if (createdAbsence.id) {
								await workflowsService.update({
									...createdAbsence,
									comments: workflow.comments,
									status: wfStatus.approved,
									shift_id:
										createdAbsence.shift?.id || workflow.shift_id,
								});
							}
						}

						return createResponse;
					})
				);
				response.ok = response.every((item) => item.ok);
			} else if (hasFiles) {
				// new workflow (with attachments)
				var blob = new Blob([JSON.stringify(absence)], {
					type: "application/json",
				});
				formData.append("workflow", blob, "workflow.json");
				response = await workflowsService.addWithAttachments(formData);
			} else {
				response = await workflowsService.add(absence);
			}

			// AUTO-APROBACIÓN si la creación fue OK.
			// Nota: ni Admin ni Supervisor se auto-aprueban cuando solicitan para sí mismos.
			if (response.ok && shouldAutoApprove && !isMultiplePeriodCreation) {
				const createdAbsence = response.data || response; // Algunos endpoints devuelven el objeto directo
				if (createdAbsence && createdAbsence.id) {
					// Preparar objeto para aprobación (status: 'Aprobado')
					const approveObj = {
						...createdAbsence,
						status: wfStatus.approved,
						shift_id: createdAbsence.shift?.id || absence.shift_id,
					};
					await workflowsService.update(approveObj);
				}
			}
		}

		if (isMultiplePeriodCreation && Array.isArray(response)) {
			const failedResponses = response
				.map((item, index) => ({ item, index }))
				.filter(({ item }) => item.status < 200 || item.status >= 300);

			if (failedResponses.length > 0) {
				hasErrors = true;
				failedPeriods = failedResponses.map(
					({ index }) => this.state.absencePeriods[index]
				);
				const errors = failedResponses.reduce(
					(all, { item }) => all.concat(item.errors || []),
					[]
				);
				errorMsg = errorFormating({ errors });
				if (!errorMsg.general) {
					errorMsg.general = this.props.t("absences.form.somePeriodsFailed");
				}
			}
		}

		if (!response.ok && !hasErrors) {
			hasErrors = true;
			errorMsg = errorFormating(response);
		}

		//console.log(response);

		if (!hasErrors) {
			const savedAbsence = { ...this.state.absence };

			this.setState(
				{
					isLoading: false,
					errors: {},
				},
				async () => {
					toastr.success(this.props.t("common.success"), this.props.t("common.changesSaved"));

					const targetId =
						this.state.creationTarget === "employee"
							? this.state.selectedEmployeeId
							: this.state.absence.employee_id || this.props.user.id;

					// Intelligent refresh based on the active tab
					if (this.props.activeTab === "team") {
						// Always refresh in team tab if an admin creates for someone
						if (this.props.refreshData) this.props.refreshData();
					} else if (
						this.props.viewedEmployeeId === undefined ||
						targetId == this.props.viewedEmployeeId
					) {
						// Only refresh in other tabs if it affects the viewed employee
						if (this.props.refreshData) await this.props.refreshData();
					}

					if (this.props.onPlanningSaveComplete) {
						await this.props.onPlanningSaveComplete(savedAbsence);
					}

					this.props.handleClose();

					if (isAdmin() || isSupervisor()) {
						this.props.getPendingAbsences();
						this.props.getManagedAbsences();
					}
				}
			);
		} else {
			const nextErrorState = {
				isLoading: false,
				submitted: true,
				errors: errorMsg,
			};
			if (failedPeriods && failedPeriods.length > 0) {
				nextErrorState.absencePeriods = failedPeriods;
				nextErrorState.absence = {
					...absence,
					start: failedPeriods[0].start,
					end: failedPeriods[0].end,
				};
			}
			this.setState(nextErrorState);
		}
	};

	onDrop = (files) => {
		this.setState({ files });
	};

	removeAttachment = async (attachment) => {
		const { absence } = this.state;
		// console.log(attachment);
		const response = await workflowsService.removeAttachment(
			absence.id,
			attachment.url
		);
		// console.log(response);
		if (response.ok) {
			this.setState({
				absence: {
					...absence,
					attachments: absence.attachments.filter(
						(att) => att.url !== attachment.url
					), // remove attachment from the array
				},
			});
		}
	};
	removeFile = (file) => {
		const { files } = this.state;
		// console.log(files);

		this.setState({
			// files : files.splice(i, 1) // remove the file from the array
			files: files.filter((f) => f.lastModified !== file.lastModified), // remove the file from the array
		});
	};

	getAttachment = async (attachment) => {
		const { absence } = this.state;
		// console.log(attachment);
		const response = await workflowsService.getAttachment(
			absence.id,
			attachment.url
		);
		// console.log(response);
		var url = window.URL.createObjectURL(response);
		var a = document.createElement("a");
		document.body.appendChild(a);
		a.href = url;
		a.download = attachment.name;
		a.click();
	};

	getHourlyContent = () => {
		const { absence, disabled, hourlyEnd } = this.state;
		return (
			<>
				<Row>
					<Col sm={6}>
						<FormGroup
							className="form-group form-group-time"
							controlId="start_hour"
						>
							<FormLabel>
								{this.props.t("absences.form.fromHour")} <span className="label-required">*</span>
							</FormLabel>
							<TimeInputWithArrows
								id="start_hour"
								value={
									absence.start_hour === null ||
									absence.start_hour === undefined
										? ""
										: moment.utc(absence.start_hour).local().format("HH:mm")
								}
								onChange={this.handleTimeChange}
								placeholder="HH:mm"
								disabled={disabled}
								isInvalid={this.checkValidField("start_hour")}
							/>

							<FormControl.Feedback type="invalid">
								{this.state.errors.start_hour}
							</FormControl.Feedback>
						</FormGroup>
					</Col>

					<Col sm={6}>
					<FormGroup className="form-group form-group-time" controlId="end_hour">
							<FormLabel>
								{this.props.t("absences.form.toHour")} <span className="label-required">*</span>
							</FormLabel>
							<TimeInputWithArrows
								id="end_hour"
								value={hourlyEnd}
								onChange={this.handleHourlyEndChange}
								placeholder="HH:mm"
								disabled={disabled}
								isInvalid={
									this.checkValidField("end_hour") ||
									this.checkValidField("duration")
								}
							/>

							<FormControl.Feedback type="invalid">
								{this.state.errors.end_hour || this.state.errors.duration}
							</FormControl.Feedback>
						</FormGroup>
					</Col>
				</Row>
			</>
		);
	};

	getHourlySummaryContent = () => {
		const { absence, hourlyEnd } = this.state;
		const start = absence.start_hour
			? moment.utc(absence.start_hour).local().format("HH:mm")
			: "";
		const startTime = moment(start, "HH:mm", true);
		const endTime = moment(hourlyEnd, "HH:mm", true);
		const duration = startTime.isValid() && endTime.isValid()
			? moment.duration(endTime.diff(startTime)).asMinutes()
			: 0;
		const durationLabel = duration > 0
			? `${Math.floor(duration / 60)} h${duration % 60 ? ` ${duration % 60} min` : ""}`
			: "";
		return (
			<div className="absence-hourly-summary">
				<strong className="absence-section-label">{this.props.t("absences.form.summary")}</strong>
				<div className="absence-hourly-summary-row">
					<FiClock />
					<strong>
						{absence.start ? moment(absence.start).locale(this.props.i18n.language).format("D MMM") : "--"}
						{start && hourlyEnd ? ` · ${start} – ${hourlyEnd}` : ""}
					</strong>
					<span>{durationLabel}</span>
				</div>
			</div>
		);
	};

	getSingleDayContent = () => {
		const { absence, disabled } = this.state;
		return (
			<FormGroup
				controlId="start"
				className={`form-group form-date flex-col ${absence.start === "" && "empty"} ${this.checkValidField("start") && "is-invalid"}`}
			>
				<FormLabel>
					{this.props.t("absences.form.requestedDate")} <span className="label-required">*</span>
				</FormLabel>

				<DatePicker
					selected={
						absence.start === "" || !absence.start
							? null
							: moment(absence.start).toDate()
					}
					onChange={(date) => this.handleDateChange("start", date)}
					disabled={disabled}
					isInvalid={this.checkValidField("start")}
					className="form-control"
					placeholderText="dd/mm/aaaa"
					fixedHeight
					showWeekNumbers
				/>
				<FiCalendar />
				<FormControl.Feedback type="invalid">
					{this.state.errors.start}
				</FormControl.Feedback>
			</FormGroup>
		);
	};

	getStartEndContent = () => {
		const { absence, disabled } = this.state;

		// Definimos en qué mes debe abrir el datepicker de "Termina" cuando aún no hay fecha de fin.
		let endOpenToDate = null;
		if (absence.start) {
			const startMoment = moment(absence.start);
			const isLastDayOfMonth = startMoment
				.clone()
				.endOf("month")
				.isSame(startMoment, "day");
			const baseMonth = isLastDayOfMonth
				? startMoment.clone().add(1, "month")
				: startMoment;
			endOpenToDate = baseMonth.toDate();
		}

		return (
			<Row>
				<Col sm={6}>
					<FormGroup
						controlId="start"
						className={`form-group form-date form-date-start ${absence.start === "" && "empty"}  ${this.checkValidField("start") && "is-invalid"}`}
					>
						<FormLabel>
							{this.props.t("absences.form.starts")} <span className="label-required">*</span>
						</FormLabel>
						<DatePicker
							selected={
								absence.start === "" || !absence.start
									? null
									: moment(absence.start).toDate()
							}
							onChange={(date) => this.handleDateChange("start", date)}
							disabled={disabled}
							isInvalid={this.checkValidField("start")}
							className="form-control"
							placeholderText="dd/mm/aaaa"
							fixedHeight
							showWeekNumbers
						/>
						<FiCalendar />
						<FormControl.Feedback type="invalid">
							{this.state.errors.start}
						</FormControl.Feedback>
					</FormGroup>
				</Col>

				<Col sm={6}>
					<FormGroup
						controlId="end"
						className={`form-group form-date form-date-end ${absence.end === "" && "empty"}  ${this.checkValidField("end") && "is-invalid"}`}
					>
						<FormLabel>{this.props.t("absences.form.ends")}</FormLabel>
						<DatePicker
							selected={
								absence.end === "" || !absence.end
									? absence.end
									: moment(absence.end).toDate()
							}
							openToDate={
								!absence.end && endOpenToDate ? endOpenToDate : undefined
							}
							onChange={(date) => this.handleDateChange("end", date)}
							disabled={disabled}
							isInvalid={this.checkValidField("end")}
							className="form-control"
							placeholderText="dd/mm/aaaa"
							fixedHeight
							showWeekNumbers
						/>
						<FiCalendar />
						<FormControl.Feedback type="invalid">
							{this.state.errors.end}
						</FormControl.Feedback>
					</FormGroup>
				</Col>
			</Row>
		);
	};

	getCalendarContent = () => {
		const { calendarMonth, calendarYear, disabled, isEdit } = this.state;
		const calendarReadOnly = disabled || (isEdit && !this.state.absence.workflow_hourly_requested);
		const firstDay = moment({ year: calendarYear, month: calendarMonth, date: 1 });
		const leadingBlanks = firstDay.isoWeekday() - 1;
		const daysInMonth = firstDay.daysInMonth();
		const selected = this.getEffectiveSelectedDates();
		const holidays = this.getHolidayMap();
		const todayKey = moment().format(DATE_KEY_FORMAT);
		const currentMonth = moment().startOf("month");
		const shownMonth = firstDay.clone().startOf("month");
		const canGoBack = disabled || shownMonth.isAfter(currentMonth, "month");
		const cells = [];

		for (let index = 0; index < leadingBlanks; index += 1) {
			cells.push(<span className="absence-calendar-blank" key={`blank-${index}`} />);
		}
		for (let dayNumber = 1; dayNumber <= daysInMonth; dayNumber += 1) {
			const date = firstDay.clone().date(dayNumber);
			const dateKey = date.format(DATE_KEY_FORMAT);
			const isSelected = selected.has(dateKey);
			const selectable = this.isCalendarDateSelectable(dateKey);
			const weekday = date.isoWeekday();
			const previousKey = date.clone().subtract(1, "day").format(DATE_KEY_FORMAT);
			const nextKey = date.clone().add(1, "day").format(DATE_KEY_FORMAT);
			const classNames = ["absence-calendar-day"];
			if (isSelected) classNames.push("is-selected");
			if (dateKey === todayKey) classNames.push("is-today");
			if (holidays[dateKey]) classNames.push("is-holiday");
			if (isSelected && weekday > 1 && selected.has(previousKey)) classNames.push("joins-left");
			if (isSelected && weekday < 7 && selected.has(nextKey)) classNames.push("joins-right");

			cells.push(
				<button
					type="button"
					key={dateKey}
					data-date={dateKey}
					className={classNames.join(" ")}
					disabled={calendarReadOnly || !selectable}
					title={holidays[dateKey]?.name || (!selectable ? this.props.t("absences.form.nonWorkingDay") : "")}
					aria-pressed={isSelected}
					onPointerDown={(event) => this.startCalendarDrag(dateKey, event)}
					onPointerEnter={() => this.moveCalendarDrag(dateKey)}
					onClick={() => this.handleCalendarClick(dateKey)}
				>
					{dayNumber}
				</button>
			);
		}

		return (
			<>
				<div className="absence-calendar">
					<div className="absence-calendar-header">
						<button type="button" className="btn btn-icon" disabled={!canGoBack} onClick={() => this.changeCalendarMonth(-1)} aria-label={this.props.t("absences.form.previousMonth")}>
							<FiChevronLeft />
						</button>
						<strong>{firstDay.locale(this.props.i18n.language).format("MMMM YYYY")}</strong>
						<button type="button" className="btn btn-icon" onClick={() => this.changeCalendarMonth(1)} aria-label={this.props.t("absences.form.nextMonth")}>
							<FiChevronRight />
						</button>
					</div>
					<div className="absence-calendar-weekdays">
						{moment.weekdaysMin(true).map((weekday) => <span key={weekday}>{weekday.slice(0, 1)}</span>)}
					</div>
					<div className="absence-calendar-days">{cells}</div>
					<div className="absence-calendar-legend">
						<span><i className="selected" />{this.props.t("absences.form.selectedLegend")}</span>
						<span><i className="today" />{this.props.t("absences.form.todayLegend")}</span>
						<span><i className="holiday" />{this.props.t("absences.form.holidayLegend")}</span>
					</div>
				</div>
			</>
		);
	};

	getMultiplePeriodsContent = () => {
		const { absencePeriods, disabled, editingPeriod, selectedDates } = this.state;
		return (
			<div className="absence-periods">
				<div className="absence-periods-heading">
					<strong>{this.props.t("absences.form.requestedPeriods")}</strong>
					<span>{selectedDates.length > 0 && this.props.t("absences.form.totalDays", { count: selectedDates.length })}</span>
					{!disabled && selectedDates.length > 0 && <button type="button" className="btn btn-link" onClick={this.clearSelectedDates}>{this.props.t("absences.form.clearAll")}</button>}
				</div>
				{absencePeriods.length === 0 && <div className="absence-periods-empty">{this.props.t("absences.form.noDaysSelected")}</div>}
				{absencePeriods.map((period, index) => {
					const count = this.getSelectableDateRange(period.start, period.end, true).length;
					const label = period.start === period.end
						? moment(period.start).format("D MMM")
						: `${moment(period.start).format("D MMM")} – ${moment(period.end).format("D MMM")}`;
					if (editingPeriod === index && !disabled) {
						return (
							<div className="absence-period-edit" key={`${period.start}-${index}`}>
								<strong>{this.props.t("absences.form.editPeriod")}</strong>
								<div className="absence-period-edit-fields">
									<FormGroup className="form-date flex-col">
										<FormLabel>{this.props.t("absences.form.starts")}</FormLabel>
										<DatePicker
											selected={period.start ? moment(period.start).toDate() : null}
											onChange={(date) => this.handlePeriodDateChange(index, "start", date)}
											className="form-control"
											placeholderText="dd/mm/aaaa"
											fixedHeight
											showWeekNumbers
										/>
										<FiCalendar />
									</FormGroup>
									<FormGroup className="form-date flex-col">
										<FormLabel>{this.props.t("absences.form.ends")}</FormLabel>
										<DatePicker
											selected={period.end ? moment(period.end).toDate() : null}
											onChange={(date) => this.handlePeriodDateChange(index, "end", date)}
											minDate={period.start ? moment(period.start).toDate() : undefined}
											className="form-control"
											placeholderText="dd/mm/aaaa"
											fixedHeight
											showWeekNumbers
										/>
										<FiCalendar />
									</FormGroup>
								</div>
								<div className="absence-period-edit-actions"><button type="button" className="btn btn-link" onClick={this.cancelPeriodEdit}>{this.props.t("common.cancel")}</button><button type="button" className="btn btn-primary" onClick={() => this.applyPeriodEdit(index)}>{this.props.t("absences.form.apply")}</button></div>
							</div>
						);
					}
					return (
						<div className="absence-period-row" key={`${period.start}-${index}`}>
							<FiCalendar />
							<strong>{label}</strong>
							<span>{this.props.t("absences.form.dayCount", { count })}</span>
							{!disabled && <button type="button" className="btn btn-icon" onClick={() => this.setState({ editingPeriod: index, editingPeriodOriginal: { ...period } })} aria-label={this.props.t("absences.form.editPeriod")}><FiEdit2 /></button>}
							{!disabled && <button type="button" className="btn btn-icon" onClick={() => this.removeAbsencePeriod(index)} aria-label={this.props.t("absences.form.removePeriod")}><FiX /></button>}
						</div>
					);
				})}
			</div>
		);
	};

	render() {
		const { absence, disabled, errors, overlappingByDate, isLoadingOverlap } =
			this.state;
		const { shifts = [] } = this.props;
		const hasOverlap = Object.keys(overlappingByDate).length > 0;

		// Determinamos los turnos a mostrar priorizando el contrato del empleado si es para "otro"
		let finalShifts = [];
		const isSelf =
			this.state.creationTarget === "self" ||
			(this.state.isEdit &&
				(absence?.requester?.id || absence?.requester_id) ===
					this.props.user.id);

		const employee = this.props.employee;
		const contract =
			employee?.employee_with_contract?.contract ||
			employee?.employeeWithContract?.contract;

		const contractShifts =
			contract?.allowed_absence_shifts ||
			contract?.allowedAbsenceShifts ||
			employee?.allowed_absence_shifts ||
			employee?.allowedAbsenceShifts;

		if (!isSelf && contractShifts && contractShifts.length > 0) {
			finalShifts = contractShifts;
		} else {
			// Fallback a los turnos generales (shiftsReducer) para "mismo usuario" o si no hay contrato específico
			// Nos aseguramos de que shifts sea un array (en algunos casos puede venir como el objeto de respuesta completo si el action no extrajo .data)
			finalShifts = Array.isArray(shifts) ? shifts : shifts?.data || [];
		}

		let optionList = finalShifts.map((item, i) => {
			const label = item.description
				? item.name + ", " + item.description
				: item.name;
			return (
				<option key={i} value={item.id}>
					{label}
				</option>
			);
		});

		// already saved attachments
		const attachments =
			absence.attachments &&
			absence.attachments.map((attachment) => {
				var size = attachment.size / 1000000;
				var sizeLetters = "Mb";
				if (size < 1) {
					size = attachment.size / 1000;
					sizeLetters = "Kb";
				}
				return (
					<li className="file-item" key={attachment.url}>
						<span
							className="file-info"
							title={attachment.name}
							onClick={() => this.getAttachment(attachment)}
						>
							<span className="file-name">{attachment.name}</span>
							<span className="file-size">
								({size.toFixed(2)}
								{sizeLetters})
							</span>
						</span>
						{!disabled && (
							<div className="file-action">
								<MdPlaylistAddCheck className="file-saved" />
								<button
									type="button"
									className="btn btn-icon"
									title={this.props.t("common.delete")}
									onClick={() => this.removeAttachment(attachment)}
								>
									<FiTrash2 />
								</button>
							</div>
						)}
					</li>
				);
			});

		// temporary dropzone files
		const files =
			this.state.files.length > 0 &&
			this.state.files.map((file, i) => {
				var size = file.size / 1000000;
				var sizeLetters = "Mb";
				if (size < 1) {
					size = file.size / 1000;
					sizeLetters = "Kb";
				}
				return (
					<li className="file-item-temp" key={i}>
						<span className="file-info" title={file.name}>
							<span className="file-name">{file.name}</span>
							<span className="file-size">
								({size.toFixed(2)}
								{sizeLetters})
							</span>
						</span>
						{!disabled && (
							<div className="file-action">
								<button
									type="button"
									className="btn btn-icon"
									title={this.props.t("common.delete")}
									onClick={() => this.removeFile(file)}
								>
									<FiTrash2 />
								</button>
							</div>
						)}
					</li>
				);
			});

		const { isEdit, creationStep, creationTarget, selectedEmployeeId } =
			this.state;
		const { select_employees = [] } = this.props;

		// ── Paso de selección inicial ────────────────────────────────────────────
		if (creationStep === "select") {
			return (
				<SimpleModal
					isOpen
					onClose={this.props.handleClose}
					className="absence-simple-modal absence-step-modal"
				>
					<div
						className="p-4 d-flex flex-column"
						style={{ minHeight: "320px" }}
					>
						<div className="modal-header border-0 p-0 mb-4">
							<h3 className="modal-title">
								{this.props.t("absences.form.whoFor")}
							</h3>
						</div>
						<div className="modal-body p-0 flex-grow-1">
							<p className="text-secondary">
								{this.props.t("absences.form.supervisorInfo")}
							</p>
						</div>
						<div className="modal-footer border-0 p-0 d-flex justify-content-end gap-3 mt-auto">
							<button
								className="btn btn-outline-primary px-4 py-2"
								onClick={() => this.handleSelectTarget("self")}
							>
								{this.props.t("absences.form.requestForMe")}
							</button>
							<button
								className="btn btn-primary px-4 py-2"
								onClick={() => this.handleSelectTarget("employee")}
							>
								{this.props.t("absences.form.requestForEmployee")}
							</button>
						</div>
					</div>
				</SimpleModal>
			);
		}

		// ── Paso de selección de empleado ────────────────────────────────────────
		if (creationStep === "employee-select") {
			return (
				<SimpleModal
					isOpen
					onClose={this.props.handleClose}
					className="absence-simple-modal absence-step-modal"
				>
					<div
						className="p-4 d-flex flex-column"
						style={{ minHeight: "320px" }}
					>
						<div className="modal-header border-0 p-0 mb-4">
							<h3 className="modal-title">{this.props.t("absences.form.selectEmployee")}</h3>
						</div>
						<div className="modal-body p-0 flex-grow-1">
							<p className="text-secondary mb-4">
								{this.props.t("absences.form.selectEmployeeInfo")}
							</p>

							<FormGroup
								className="form-group w-100"
								controlId="employee_select"
							>
								<EmployeeSelectSearch
									className="employee-select-search--fullwidth"
									controlId="employee_select"
									multiple={false}
									fetchOptions={this.employeeSelectFetchOptions}
									value={
										selectedEmployeeId === null
											? null
											: String(selectedEmployeeId)
									}
									onChange={(idOrNull) =>
										this.setState({
											selectedEmployeeId: idOrNull ? Number(idOrNull) : null,
										})
									}
									placeholder={this.props.t("absences.form.searchEmployee")}
									allLabel={this.props.t("absences.form.searchEmployee")}
									allowClear={false}
								/>
							</FormGroup>
						</div>

						<div className="modal-footer border-0 p-0 d-flex justify-content-end gap-3">
							<button
								type="button"
								onClick={() => this.setState({ creationStep: "select" })}
								className="btn btn-outline-primary px-4 py-2 d-flex align-items-center gap-2"
							>
								<FiArrowLeft /> {this.props.t("common.back")}
							</button>
							<button
								type="button"
								className="btn btn-primary px-4 py-2"
								disabled={!selectedEmployeeId}
								onClick={() => {
									this.setState({ creationStep: "form" });
									if (this.props.employee?.id !== selectedEmployeeId) {
										this.props.getEmployee(selectedEmployeeId);
									}
								}}
							>
								{this.props.t("common.next")}
							</button>
						</div>
					</div>
				</SimpleModal>
			);
		}

		const selectedEmployee = select_employees.find(
			(e) => String(e.id) === String(selectedEmployeeId)
		);
		const employee_fullname = selectedEmployee
			? `${selectedEmployee.name} ${selectedEmployee.surname}`
			: "";

		// ── Columna izquierda: formulario ────────────────────────────────────────
		const leftContent = (
			<>
				<h5
					className={`modal-title ${creationTarget === "employee" && employee_fullname ? "mb-1" : "mb-3"}`}
				>
					{disabled
						? this.props.t("absences.viewAbsence")
						: isEdit
							? this.props.t("absences.editAbsence")
							: this.props.t("absences.form.newAbsence")}
				</h5>
				{creationTarget === "employee" && employee_fullname && (
					<div className="mb-3 text-secondary" style={{ fontSize: "0.9rem" }}>
						{this.props.t("absences.form.employee")}: <strong>{employee_fullname}</strong>
					</div>
				)}
				<form onSubmit={this.handleSubmit} className="absence-form-scroll">
					<div className={`absence-edit ${disabled ? "disabled" : ""}`}>
						<div className="absence-request-grid">
							<div className="absence-request-left">
								<FormGroup className="form-group" controlId="absenceType">
									<FormLabel>{this.props.t("absences.form.requestBy")}</FormLabel>
									<div className="btn-choice-group">
										<FormCheck type="radio" id="days" label={this.props.t("absences.form.days")} value="false" disabled={disabled} checked={!absence.workflow_hourly_requested} bsPrefix={!absence.workflow_hourly_requested ? "btn-choice active" : "btn-choice"} onChange={this.handleTypeChange} />
										<FormCheck type="radio" id="hours" label={this.props.t("absences.form.hours")} value="true" disabled={disabled} checked={absence.workflow_hourly_requested} bsPrefix={absence.workflow_hourly_requested ? "btn-choice active" : "btn-choice"} onChange={this.handleTypeChange} />
									</div>
								</FormGroup>

								<FormGroup className="form-group" controlId="shift_id">
									<FormLabel>{this.props.t("absences.form.absenceLabel")} <span className="label-required">*</span></FormLabel>
									<FormControl
										type="text"
										value={absence.shift_id}
										disabled={disabled || (isSelf ? this.props.loading_shifts : this.props.loading_employee)}
										onChange={this.handleChange}
										isInvalid={this.checkValidField("shift_id")}
										as="select"
									>
										<option value="">{(isSelf ? this.props.loading_shifts : this.props.loading_employee) ? this.props.t("common.loading") : this.props.t("absences.form.selectAbsence")}</option>
										{optionList}
									</FormControl>
									<FormControl.Feedback type="invalid">{errors.shift_id}</FormControl.Feedback>
								</FormGroup>

								{this.getCalendarContent()}
								{errors.start && <p className="error-message absence-date-error">{errors.start}</p>}
							</div>

							<div className={`absence-request-right ${absence.workflow_hourly_requested ? "is-hourly" : "is-days"}`}>
								{absence.workflow_hourly_requested ? (
									<>
										{this.getHourlyContent()}
										{this.getHourlySummaryContent()}
									</>
								) : this.getMultiplePeriodsContent()}

						<FormGroup className="form-group absence-comments" controlId="comments">
							<FormLabel>{this.props.t("absences.form.comments")}</FormLabel>
							<FormControl
								as="textarea"
								value={absence.comments || ""}
								onChange={this.handleChange}
								placeholder={this.props.t("absences.form.commentsPlaceholder")}
								disabled={disabled}
								isInvalid={this.checkValidField("comments")}
							/>
							<FormControl.Feedback type="invalid">
								{errors.comments}
							</FormControl.Feedback>
						</FormGroup>

						{(!disabled || (disabled && attachments.length > 0)) && (
							<>
								<label className="form-label">
									{disabled ? this.props.t("absences.form.attachedDocuments") : this.props.t("absences.form.attachDocuments")}
								</label>
								<Dropzone onDrop={this.onDrop}>
									{({ getRootProps, getInputProps }) => (
										<section className="dropzone-container">
											{!disabled && (
												<div {...getRootProps({ className: "dropzone" })}>
													<input {...getInputProps()} />
													<p>
														{this.props.t("absences.form.dropzoneText")}
													</p>
												</div>
											)}
											<ul className="dropzone-files">
												{attachments}
												{files}
											</ul>
										</section>
									)}
								</Dropzone>
							</>
						)}
						{absence.answer_comments && (
							<FormGroup
								controlId="answer_comments"
								className="form-group mt25 mb10"
							>
								<FormControl
									as="textarea"
									value={absence.answer_comments}
									onChange={this.handleChange}
									placeholder={this.props.t("absences.form.comments")}
									disabled={disabled}
									className="answer-comments"
									isInvalid={this.checkValidField("answer_comments")}
								/>
							</FormGroup>
						)}

						{errors.allow_hourly_absence_request && (
							<p className="error-message">
								{errors.allow_hourly_absence_request}
							</p>
						)}
						{errors.general && (
							<p className="error-message">{errors.general}</p>
						)}
							</div>
						</div>
					</div>

					<div className="modal-footer d-flex justify-content-end gap-3">
						{!isEdit && creationTarget === "employee" && (
							<button
								type="button"
								onClick={() =>
									this.setState({ creationStep: "employee-select" })
								}
								className="btn btn-outline-primary px-4 py-2 d-flex align-items-center gap-2"
							>
								<FiArrowLeft /> {this.props.t("common.back")}
							</button>
						)}
						<button
							type="button"
							onClick={this.props.handleClose}
							className="btn btn-outline-primary px-4 py-2"
						>
							{this.props.t("common.cancel")}
						</button>
						{!disabled && (
							<SubmitButton
								type="submit"
								isLoading={this.state.isLoading}
								disabled={!absence.workflow_hourly_requested && this.state.selectedDates.length === 0}
								text={this.props.t("common.save")}
								loadingText={this.props.t("common.saving")}
							/>
						)}
					</div>
				</form>
			</>
		);

		// ── Columna derecha: coincidencias ───────────────────────────────────────
		const rightContent = (
			<div className="absence-overlap-right">
				<h5 className="overlap-panel-title">{this.props.t("absences.form.overlaps")}</h5>
				{isLoadingOverlap ? (
					<div className="overlap-loading">{this.props.t("absences.form.searching")}</div>
				) : hasOverlap ? (
					<div className="overlap-list">
						{Object.entries(overlappingByDate).map(([date, workers]) => (
							<div key={date} className="overlap-day-group">
								<p className="overlap-day-header">
									{moment(date).format("DD/MM/YYYY")}
								</p>
								{workers.map((w) => {
									const isPending =
										w.status === wfStatus.pending || w.status === "PENDING";
									const position =
										w.requester.position || w.requester.function_name || null;
									return (
										<div
											key={w.id || w.requester.id}
											className="overlap-employee-row"
										>
											<Avatar
												userId={w.requester.id}
												alt={w.requester.name}
												className="overlap-avatar-img"
											/>
											<div className="overlap-employee-text">
												<span className="overlap-employee-name">
													{w.requester.name} {w.requester.surname}
												</span>
												{position && (
													<span className="overlap-employee-role">
														{position}
													</span>
												)}
											</div>
											{isPending && (
												<span
													className="overlap-status-dot"
													title={this.props.t("absences.status.pending")}
													aria-label={this.props.t("absences.status.pending")}
												/>
											)}
										</div>
									);
								})}
							</div>
						))}
					</div>
				) : null}
			</div>
		);

		// ── SimpleModal — DOM fijo con transición CSS ─────────────────────────────
		return (
			<SimpleModal
				isOpen
				onClose={this.props.handleClose}
				className={`absence-simple-modal absence-simple-modal-scroll${hasOverlap ? " has-overlap" : ""}`}
			>
				<div className="absence-layout">
					{/* Columna izquierda: formulario (siempre visible) */}
					<div className="absence-layout-form">{leftContent}</div>

					{/* Columna derecha: panel de coincidencias (se anima vía CSS) */}
					{/* Solo visible para Admins y Supervisores */}
					{(isAdmin() || isSupervisor()) && (
						<div
							className={`absence-layout-panel${hasOverlap ? " expanded" : ""}`}
						>
							{rightContent}
						</div>
					)}
				</div>
			</SimpleModal>
		);
	}
}

const mapStateToProps = (reducers) => {
	return {
		shifts: reducers.shiftsReducer.shifts,
		loading_shifts: reducers.shiftsReducer.loading_shifts,
		...reducers.absencesReducer,
		employee: reducers.employeesReducer.employee,
		loading_employee: reducers.employeesReducer.loading,
		select_employees: reducers.employeesReducer.select_employees,
		user: reducers.authReducer.user,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getEmployeeAbsences: (employee_id) =>
		dispatch(employeesActions.getEmployeeAbsences(employee_id)),
	getPendingAbsences: (options) =>
		dispatch(workflowsActions.getPendingAbsences(options)),
	getManagedAbsences: (options) =>
		dispatch(workflowsActions.getManagedAbsences(options)),
	getAbsenceShifts: (options) =>
		dispatch(
			shiftsActions.getAllShifts({
				shift_type: shiftTypeQuery.absence,
				contract_only: true,
				...options,
			})
		),
	getSelectableEmployees: () =>
		dispatch(employeesActions.getSelectableEmployees()),
	getEmployee: (id) => dispatch(employeesActions.getEmployee(id)),
	clearEmployeeState: () => dispatch(employeesActions.clearEmployeeState()),
	getEmployeeAbsenceCalendar: (employee_id, year) =>
		dispatch(employeesActions.getEmployeeAbsenceCalendar(employee_id, year)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(AbsenceEdit));
